import { Button, Message } from '@alifd/next';
import {
  Cascader,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Space,
  Typography,
} from 'antd';
import locale from 'antd/es/date-picker/locale/zh_TW';
import React, { useMemo, useRef, useState } from 'react';

import ButtonWithDialog from '@/components/UI/ButtonWithDialog';
import client from '@/lib/feathers';

const { Item, useForm } = Form;
const { TextArea } = Input;

const financeCommunity = client.service('financeCommunity');
const accountingSubjects = client.service('accountingSubjects');

const tradeTypeTitles = {
  income: {
    creditTitle: '收入種類',
    debitTitle: '存入帳戶',
  },
  expense: {
    creditTitle: '支出帳戶',
    debitTitle: '支出種類',
  },
  transfer: {
    creditTitle: '轉出帳戶',
    debitTitle: '轉入帳戶',
  },
};

const tradeTypeOptions = [
  {
    value: 'income',
    label: '收入',
  },
  {
    value: 'expense',
    label: '支出',
  },
  {
    value: 'transfer',
    label: '轉帳',
  },
];

export default function CreateRecordDialog({ nestedSubjects, setIsUpdate }) {
  const [form] = useForm();
  const [tradeType, setTradeType] = useState('income');
  const [uploadPath, setUploadPath] = useState('');
  const [uploadData, setUploadData] = useState(null);
  const [showCreateRecordDialog, setShowCreateRecordDialog] = useState(false);

  const uploadRef = useRef(null);

  const CascadeSubjects = useMemo(() => {
    const subjects = [];
    nestedSubjects.forEach((subject) => {
      const children = [];
      if (subject.children) {
        subject.children.forEach((child) => {
          if (child.activated) {
            children.push({
              value: child._id,
              label: `${child.subjectCode} ${child.subjectName}`,
            });
          }
        });
      }
      if (subject.activated) {
        subjects.push({
          value: subject._id,
          label: `${subject.subjectCode} ${subject.subjectName}`,
          children,
        });
      }
    });

    switch (tradeType) {
      case 'income':
        return {
          debitList: subjects.filter(
            (subject) => subject.label.slice(0, 1) === '1'
          ),
          creditList: subjects.filter(
            (subject) => subject.label.slice(0, 1) === '4'
          ),
        };
      case 'expense':
        return {
          debitList: subjects.filter((subject) =>
            ['2', '6'].includes(subject.label.slice(0, 1))
          ),
          creditList: subjects.filter(
            (subject) => subject.label.slice(0, 1) === '1'
          ),
        };
      case 'transfer':
        return {
          debitList: subjects.filter(
            (subject) => subject.label.slice(0, 1) === '1'
          ),
          creditList: subjects.filter(
            (subject) => subject.label.slice(0, 1) === '1'
          ),
        };
      default:
        break;
    }
  }, [nestedSubjects, tradeType]);

  const clearFormValues = () => {
    form.resetFields();
    setUploadPath('');
    setUploadData(null);
  };

  const handleCreateTradeRecord = async (value) => {
    const {
      tradeDate,
      tradeType,
      amount,
      remark,
      creditSubjectId,
      debitSubjectId,
    } = value;
    const tradeRecord = {
      tradeDate: tradeDate.valueOf(),
      tradeType,
      creditSubjectId: creditSubjectId.at(-1),
      debitSubjectId: debitSubjectId.at(-1),
      amount,
      remark,
    };
    try {
      let newAttachment = null;
      const body = new FormData();
      body.append('files', uploadData);
      if (uploadData) {
        if (uploadData.size / 1024 / 1024 > 20) {
          return Message.error('上傳檔案不能超過20MB');
        }
        const res = await fetch('/newUploads?type=financeCommunity', {
          method: 'POST',
          body,
          headers: { authorization: window.localStorage['feathers-jwt'] },
        });

        const json = await res.json();
        const { showPath, _id } = json[0];

        newAttachment = {
          path: showPath,
          name: uploadData.name,
          id: _id,
        };
      }

      tradeRecord.attachment = newAttachment;

      await financeCommunity.create(tradeRecord);

      await accountingSubjects.patch(creditSubjectId.at(-1), { used: true });
      await accountingSubjects.patch(debitSubjectId.at(-1), { used: true });

      clearFormValues();
      setIsUpdate(true);
      setShowCreateRecordDialog(false);
      Message.success('新增交易成功');
    } catch (error) {
      Message.error('新增交易失敗');
      console.error({ error });
    }
  };
  return (
    <ButtonWithDialog
      buttonTitle="新增交易記錄"
      visible={showCreateRecordDialog}
      setVisible={setShowCreateRecordDialog}
      clearFormValues={clearFormValues}
    >
      <Space direction="vertical" style={{ marginTop: '1rem', width: '400px' }}>
        <Form
          form={form}
          style={{ width: '400px' }}
          onFinish={handleCreateTradeRecord}
        >
          <Item
            label="交易類型"
            style={{ marginLeft: '11.086px' }}
            name="tradeType"
          >
            <Radio.Group
              options={tradeTypeOptions}
              onChange={(e) => {
                setTradeType(e.target.value);
                form.setFieldsValue({
                  creditSubjectId: [],
                  debitSubjectId: [],
                });
              }}
              value={tradeType}
              optionType="button"
            />
          </Item>
          <Item
            name="tradeDate"
            label="交易日期"
            rules={[{ required: true, message: '請輸入交易日期' }]}
          >
            <DatePicker locale={locale} />
          </Item>
          <Item
            name="creditSubjectId"
            label={tradeTypeTitles[tradeType].creditTitle}
            rules={[
              {
                required: true,
                message: `請輸入${tradeTypeTitles[tradeType].creditTitle}`,
              },
            ]}
          >
            <Cascader
              options={CascadeSubjects.creditList}
              expandTrigger="hover"
            />
          </Item>
          <Item
            name="amount"
            label="交易金額"
            rules={[{ required: true, message: '請輸入交易金額' }]}
          >
            <InputNumber min={1} />
          </Item>
          <Item
            name="debitSubjectId"
            label={tradeTypeTitles[tradeType].debitTitle}
            rules={[
              {
                required: true,
                message: `請輸入${tradeTypeTitles[tradeType].debitTitle}`,
              },
            ]}
          >
            <Cascader
              options={CascadeSubjects.debitList}
              expandTrigger="hover"
            />
          </Item>
          <Item
            name="remark"
            label="交易內容"
            rules={[{ required: true, message: '請輸入交易內容' }]}
          >
            <TextArea rows={3} />
          </Item>
          <Item
            label="上傳附件"
            style={{
              marginLeft: '11.086px',
              height: '60px',
              marginTop: '1rem',
            }}
          >
            <Space wrap>
              <Button
                onClick={() => {
                  uploadRef.current.click();
                }}
              >
                上傳附件
              </Button>
              {uploadPath && (
                <>
                  <Typography.Text type="secondary">
                    {uploadData.name}
                  </Typography.Text>
                  <Button
                    text
                    onClick={() => {
                      setUploadData(null);
                      setUploadPath('');
                    }}
                    style={{ opacity: 0.45 }}
                  >
                    清除
                  </Button>
                </>
              )}
            </Space>
            <input
              ref={uploadRef}
              type="file"
              id="upload"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files[0];
                setUploadData(file);
                setUploadPath(e.target.value);
              }}
            />
          </Item>
          <Item
            style={{
              margin: 0,
            }}
          >
            <Space
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'end',
                alignItems: 'end',
              }}
            >
              <Button type="primary" htmlType="submit">
                確定
              </Button>
            </Space>
          </Item>
        </Form>
      </Space>
    </ButtonWithDialog>
  );
}
